/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Form, FormInstance } from 'antd';
import { useAppDispatch } from '@/hooks/useRedux';
import { deliverOtp } from '@/redux/auth/registration/thunk';
import localforage from 'localforage';

interface Step4ContentProps {
  form: FormInstance;
}

const DIGITS = 6;
const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

function cn(...cls: Array<string | false | undefined | null>) {
  return cls.filter(Boolean).join(' ');
}

export default function Step4Content({ form }: Step4ContentProps) {
  const dispatch = useAppDispatch();

  const durationSec = 180;
  const [timeLeft, setTimeLeft] = useState<number>(durationSec);
  const [resending, setResending] = useState(false);

  const intervalRef = useRef<number | null>(null);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const [digits, setDigits] = useState<string[]>(Array(DIGITS).fill(''));

  const otp = useMemo(() => digits.join(''), [digits]);

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    setTimeLeft(durationSec);

    const id = window.setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearTimer();
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    intervalRef.current = id;
  }, [clearTimer]);

  useEffect(() => {
    startTimer();
    return () => clearTimer();
  }, [startTimer, clearTimer]);

  const canResend = timeLeft === 0 && !resending;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = pad2(timeLeft % 60);
  const isExpiringSoon = timeLeft <= 10 && timeLeft > 0;

  const expiryText = useMemo(() => {
    if (timeLeft === 0) return 'The code has expired';
    return `Expires in ${minutes}:${seconds}`;
  }, [timeLeft, minutes, seconds]);

  const expiryColor =
    timeLeft === 0
      ? 'text-red-600'
      : isExpiringSoon
        ? 'text-amber-600'
        : 'text-[#111827]';

  useEffect(() => {
    inputRefs.current[0]?.focus?.();
  }, []);

  const focusIndex = useCallback((idx: number) => {
    const next = Math.max(0, Math.min(DIGITS - 1, idx));
    inputRefs.current[next]?.focus?.();
  }, []);

  const clearAll = useCallback(() => {
    setDigits(Array(DIGITS).fill(''));
    focusIndex(0);
    form?.setFieldsValue?.({ otp: '' });
  }, [focusIndex, form]);

  useEffect(() => {
    if (otp.replace(/\D/g, '').length === DIGITS) {
      form?.setFieldsValue?.({ otp });
    }
  }, [otp, form]);

  const setDigit = useCallback((idx: number, val: string) => {
    setDigits((prev) => {
      const next = prev.slice();
      next[idx] = val;
      return next;
    });
  }, []);

  const setManyFrom = useCallback(
    (startIdx: number, incoming: string) => {
      const cleaned = incoming.replace(/\D/g, '').slice(0, DIGITS - startIdx);
      if (!cleaned) return;

      setDigits((prev) => {
        const next = prev.slice();
        for (let i = 0; i < cleaned.length; i++) {
          next[startIdx + i] = cleaned[i];
        }
        return next;
      });

      const last = Math.min(startIdx + cleaned.length - 1, DIGITS - 1);
      focusIndex(last);

      const joined = (digits.slice(0, startIdx).join('') + cleaned).replace(
        /\D/g,
        '',
      );
      if (joined.length >= DIGITS) {
        const full = Array(DIGITS)
          .fill('')
          .map((_, i) =>
            i < startIdx ? digits[i] : (cleaned[i - startIdx] ?? ''),
          )
          .join('');
        form?.setFieldsValue?.({ otp: full });
      }
    },
    [focusIndex, form, digits],
  );

  const handleContainerPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const text = e.clipboardData.getData('text') || '';
      const run = text.match(/(?<!\d)\d{6}(?!\d)/);
      const cleaned = (run ? run[0] : text.replace(/\D/g, '')).slice(
        0,
        DIGITS,
      );
      if (!cleaned) return;

      e.preventDefault();
      const nextDigits = Array(DIGITS)
        .fill('')
        .map((_, i) => cleaned[i] ?? '');

      setDigits(nextDigits);
      focusIndex(Math.min(cleaned.length - 1, DIGITS - 1));

      if (cleaned.length === DIGITS) {
        form?.setFieldsValue?.({ otp: cleaned });
      }
    },
    [focusIndex, form],
  );

  const handleChange = useCallback(
    (idx: number, raw: string) => {
      const val = (raw || '').replace(/\s+/g, '');
      if (!val) {
        setDigit(idx, '');
        return;
      }

      if (val.length > 1) {
        setManyFrom(idx, val);
        return;
      }

      const digit = val.replace(/\D/g, '');
      if (!digit) return;

      setDigit(idx, digit);

      if (idx < DIGITS - 1) focusIndex(idx + 1);

      const nextOtp = digits.map((d, i) => (i === idx ? digit : d)).join('');
      if (nextOtp.replace(/\D/g, '').length === DIGITS) {
        form?.setFieldsValue?.({ otp: nextOtp });
      }
    },
    [digits, focusIndex, form, setDigit, setManyFrom],
  );

  const handleKeyDown = useCallback(
    (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      const key = e.key;

      if (key === 'Enter') {
        e.preventDefault();
        form?.setFieldsValue?.({ otp });
        form?.submit?.();
        return;
      }

      if (key === 'ArrowLeft') {
        e.preventDefault();
        focusIndex(idx - 1);
        return;
      }
      if (key === 'ArrowRight') {
        e.preventDefault();
        focusIndex(idx + 1);
        return;
      }

      if (key === 'Home') {
        e.preventDefault();
        focusIndex(0);
        return;
      }
      if (key === 'End') {
        e.preventDefault();
        focusIndex(DIGITS - 1);
        return;
      }

      if (key === 'Delete') {
        e.preventDefault();
        setDigit(idx, '');
        return;
      }

      if (key === 'Backspace') {
        e.preventDefault();
        if (digits[idx]) {
          setDigit(idx, '');
          return;
        }
        if (idx > 0) {
          setDigit(idx - 1, '');
          focusIndex(idx - 1);
        }
      }
    },
    [digits, focusIndex, form, otp, setDigit],
  );

  const handleFirstInputAutoFill = useCallback(
    (raw: string) => {
      const cleaned = raw.replace(/\D/g, '');
      if (cleaned.length >= DIGITS) {
        const nextDigits = Array(DIGITS)
          .fill('')
          .map((_, i) => cleaned[i] ?? '');
        setDigits(nextDigits);
        focusIndex(DIGITS - 1);
        form?.setFieldsValue?.({ otp: cleaned.slice(0, DIGITS) });
        return true;
      }
      return false;
    },
    [focusIndex, form],
  );

  const handleResend = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!canResend) return;

    try {
      setResending(true);

      const session = await localforage.getItem<any>('signup.session');
      const formValues = form.getFieldsValue();

      if (!session?.session_id) return;

      const payload = {
        session_id: session.session_id,
        email_address: formValues.email,
        phone_number: formValues.phone,
      };

      await dispatch(deliverOtp(payload)).unwrap();

      clearAll();
      startTimer();
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="mx-auto w-full max-w-[450px] px-0"
      onPaste={handleContainerPaste}
    >
      <div className="flex flex-col items-center gap-3 sm:gap-4">
        <div className="flex flex-col gap-2 text-start w-full">
          <h1 className="text-[22px] sm:text-[28px] font-semibold text-[#111827]">
            Contact verification
          </h1>
          <p className="text-[13px] sm:text-[14px] text-[#6B7280] leading-relaxed">
            Input the one-time verification code sent to your contact details.
          </p>
        </div>

        <div className="w-16 h-16 rounded-2xl border border-slate-200 bg-white shadow-sm flex items-center justify-center">
          <span className="text-2xl text-slate-700" aria-hidden="true">
            ✉️
          </span>
        </div>

        <div className="flex flex-col gap-1 text-center">
          <div className="text-[18px] sm:text-[20px] font-semibold text-[#111827]">
            Check your SMS / Email
          </div>
          <div className="text-[13px] sm:text-[14px] text-[#6B7280]">
            We sent a verification code to your registered contact.
          </div>
          <div
            className={cn(
              'text-[15px] sm:text-[16px] font-semibold',
              expiryColor,
            )}
          >
            {expiryText}
          </div>
        </div>

        <Form.Item
          name="otp"
          label={null}
          preserve
          validateTrigger={['onChange', 'onBlur']}
          normalize={(val) =>
            typeof val === 'string' ? val.replace(/\s+/g, '') : val
          }
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Please enter the verification code',
            },
            {
              validator: (_, value) => {
                const v = String(value || '').replace(/\D/g, '');
                if (!v) return Promise.resolve();
                if (v.length < DIGITS)
                  return Promise.reject(
                    new Error(`Enter the full ${DIGITS}-digit code`),
                  );
                return Promise.resolve();
              },
            },
          ]}
          className="w-full mb-0!"
        >
          <div className="w-full mt-2">
            <div className="mb-3 text-[14px] sm:text-[15px] font-medium text-[#111827]">
              Type your 6 digit security code
            </div>

            <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
              {Array.from({ length: DIGITS }).map((_, idx) => {
                const char = digits[idx] ?? '';

                return (
                  <div
                    key={idx}
                    className={cn(
                      'flex-1 max-w-[52px] sm:max-w-[62px]',
                      'h-[54px] sm:h-[60px] rounded-xl border-2 bg-white',
                      'flex items-center justify-center',
                      'transition',
                      'border-[#16A34A]',
                      'focus-within:ring-2 focus-within:ring-[#86EFAC]',
                    )}
                    onClick={() => focusIndex(idx)}
                  >
                    <input
                      ref={(el) => {
                        inputRefs.current[idx] = el;
                      }}
                      value={char}
                      onChange={(e) => {
                        if (
                          idx === 0 &&
                          handleFirstInputAutoFill(e.target.value)
                        )
                          return;
                        handleChange(idx, e.target.value);
                      }}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      onPaste={(e) => {
                        const text = e.clipboardData.getData('text') || '';
                        const run = text.match(/(?<!\d)\d{6}(?!\d)/);
                        const cleaned = run ? run[0] : text.replace(/\D/g, '');
                        if (!cleaned) return;
                        e.preventDefault();
                        e.stopPropagation();
                        if (cleaned.length >= DIGITS) setManyFrom(0, cleaned);
                        else setManyFrom(idx, cleaned);
                      }}
                      onBlur={() => {
                        form?.setFieldsValue?.({ otp });
                      }}
                      inputMode="numeric"
                      autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                      maxLength={1}
                      className="
                        w-full h-full bg-transparent
                        text-center text-[18px] sm:text-[20px] font-semibold text-[#111827]
                        outline-none
                      "
                      aria-label={`OTP digit ${idx + 1}`}
                    />
                  </div>
                );
              })}
            </div>

            <div className="mt-3 flex items-center justify-between text-xs sm:text-sm">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  clearAll();
                }}
                className="text-[#6B7280] hover:text-[#111827] hover:underline"
              >
                Clear code
              </button>

              <div className="text-[#9CA3AF]">
                Tip: you can paste the full code
              </div>
            </div>
          </div>
        </Form.Item>

        <div className="w-full flex flex-col items-center gap-2 pt-2">
          <div className="flex flex-wrap justify-center items-center gap-1 text-sm">
            <span className="text-[#6B7280]">
              Didn&apos;t receive the code?
            </span>

            <a
              href="#"
              onClick={handleResend}
              className={cn(
                'font-medium',
                canResend
                  ? 'text-[var(--color-accent)] hover:underline'
                  : 'text-[#9CA3AF] cursor-not-allowed',
              )}
              aria-disabled={!canResend}
            >
              {resending ? 'Resending…' : 'Click to resend'}
            </a>
          </div>

          {timeLeft > 0 ? (
            <div className="text-[12px] sm:text-[13px] text-[#9CA3AF] text-center">
              Resend becomes available when the code expires.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
